export type DesktopExternalOpenErrorCode = "invalid-url" | "open-failed";

const MAX_EXTERNAL_URL_LENGTH = 4_096;

const ExternalOpenMessages = {
  "invalid-url": "Only valid HTTP and HTTPS addresses without embedded credentials can be opened.",
  "open-failed": "The system browser could not open this address.",
} as const satisfies Record<DesktopExternalOpenErrorCode, string>;

function hasAsciiControlCharacter(value: string): boolean {
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint <= 0x1f || codePoint === 0x7f) return true;
  }
  return false;
}

export class DesktopExternalOpenError extends Error {
  readonly code: DesktopExternalOpenErrorCode;

  constructor(code: DesktopExternalOpenErrorCode) {
    super(ExternalOpenMessages[code]);
    this.name = "DesktopExternalOpenError";
    this.code = code;
  }
}

export function normalizeExternalHttpUrl(input: string): string {
  if (
    input.length === 0 ||
    input.length > MAX_EXTERNAL_URL_LENGTH ||
    input !== input.trim() ||
    hasAsciiControlCharacter(input)
  ) {
    throw new DesktopExternalOpenError("invalid-url");
  }

  let url: URL;
  try {
    url = new URL(input);
  } catch {
    throw new DesktopExternalOpenError("invalid-url");
  }

  if (
    (url.protocol !== "http:" && url.protocol !== "https:") ||
    url.username.length > 0 ||
    url.password.length > 0
  ) {
    throw new DesktopExternalOpenError("invalid-url");
  }

  return url.toString();
}

export async function openExternalWith(
  opener: (url: string) => Promise<void>,
  input: string,
): Promise<void> {
  const url = normalizeExternalHttpUrl(input);
  try {
    await opener(url);
  } catch (error) {
    if (error instanceof DesktopExternalOpenError) throw error;
    throw new DesktopExternalOpenError("open-failed");
  }
}

export async function openExternalBrowserWindow(
  opener: (url: string, target: string, features: string) => unknown,
  input: string,
): Promise<void> {
  const url = normalizeExternalHttpUrl(input);
  try {
    opener(url, "_blank", "noopener,noreferrer");
  } catch {
    throw new DesktopExternalOpenError("open-failed");
  }
}
